<template>
  <div
    v-if="open && current"
    class="release-modal-backdrop"
    role="presentation"
    @click.self="closeCurrent"
  >
    <section
      ref="dialog"
      class="release-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`release-title-${current.id}`"
      tabindex="-1"
      @keydown="handleKeydown"
    >
      <header>
        <div>
          <span class="release-kind">{{ kindLabel(current.kind) }}</span>
          <h2 :id="`release-title-${current.id}`">{{ current.content.title }}</h2>
          <p>{{ current.content.summary }}</p>
        </div>
        <button
          type="button"
          aria-label="Fechar e marcar como lido"
          @click="closeCurrent"
        >
          ×
        </button>
      </header>

      <div class="release-modal-scroll">
        <ReleaseNoteContent :note="current" />
      </div>

      <footer>
        <span>{{ index + 1 }} de {{ notes.length }}</span>
        <button
          type="button"
          class="secondary"
          @click="openHistory"
        >
          Ver histórico
        </button>
        <button
          type="button"
          class="primary"
          @click="advance"
        >
          {{ index < notes.length - 1 ? 'Próxima novidade' : 'Concluir' }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  export default {
    name: 'ReleaseNoteModal',
    components: { ReleaseNoteContent: () => import('./ReleaseNoteContent.vue') },
    data: () => ({ open: false, index: 0, notes: [], processing: false, lastFocused: null }),
    computed: {
      ...mapState('releaseNotes', { inbox: 'inbox', available: 'available' }),
      current() {
        return this.notes[this.index];
      }
    },
    watch: {
      inbox: {
        immediate: true,
        handler(value) {
          if (!this.open && this.available && value?.length) {
            this.lastFocused = document.activeElement;
            this.notes = [...value];
            this.index = 0;
            this.open = true;
            this.$nextTick(() => this.$refs.dialog?.focus());
          }
        }
      }
    },
    beforeDestroy() {
      this.restoreFocus();
    },
    methods: {
      kindLabel(kind) {
        return { NewFeature: 'Novidade', Improvement: 'Melhoria', Fix: 'Correção', Announcement: 'Comunicado' }[kind] || kind;
      },
      async markCurrent() {
        if (!this.current || this.processing) return;
        this.processing = true;
        try {
          await this.$store.dispatch('releaseNotes/markRead', this.current);
        } catch (error) {
          console.warn('[Release Notes] Não foi possível registrar a leitura.', error);
        } finally {
          this.processing = false;
        }
      },
      async advance() {
        await this.markCurrent();
        if (this.index < this.notes.length - 1) {
          this.index += 1;
          this.$nextTick(() => this.$refs.dialog?.focus());
        } else {
          this.closeDialog();
        }
      },
      async closeCurrent() {
        await this.markCurrent();
        this.closeDialog();
      },
      async openHistory() {
        await this.markCurrent();
        this.closeDialog();
        const prefix = localStorage.getItem('prefix');
        this.$router.push({ name: 'release-notes', params: { prefix } }).catch(() => {});
      },
      handleKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCurrent();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...this.$refs.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )].filter((element) => element.offsetParent !== null);
        if (!focusable.length) {
          event.preventDefault();
          this.$refs.dialog.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      closeDialog() {
        this.open = false;
        this.$nextTick(this.restoreFocus);
      },
      restoreFocus() {
        if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
        this.lastFocused = null;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .release-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: grid;
    place-items: center;
    padding: 20px;
    background: rgba(0, 0, 0, 0.72);
  }
  .release-modal {
    width: min(900px, 100%);
    max-height: 92vh;
    display: flex;
    flex-direction: column;
    border: 1px solid #4c4541;
    border-radius: 20px;
    background: #1a1c19;
    color: white;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    header {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      padding: 24px 28px;
      border-bottom: 1px solid #4c4541;
      h2 { margin: 6px 0; font-size: 26px; }
      p { margin: 0; color: #cfc4be; }
      button { border: 0; background: transparent; color: white; font-size: 32px; align-self: flex-start; }
    }
    footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
      padding: 16px 28px;
      border-top: 1px solid #4c4541;
      span { margin-right: auto; color: #cfc4be; }
      button { border-radius: 8px; padding: 10px 16px; }
      .primary { border: 1px solid #974900; background: #974900; color: white; }
      .secondary { border: 1px solid #655c57; background: transparent; color: white; }
    }
  }
  .release-kind { color: #df8a3b; font-size: 12px; font-weight: 700; text-transform: uppercase; }
  .release-modal-scroll { overflow-y: auto; padding: 28px; }
  @media (max-width: 600px) {
    .release-modal-backdrop { padding: 0; }
    .release-modal { min-height: 100dvh; max-height: 100dvh; border-radius: 0; }
    .release-modal header, .release-modal-scroll, .release-modal footer { padding: 18px; }
    .release-modal footer { flex-wrap: wrap; }
  }
</style>
